import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import { useAuth } from "../context/AuthProvider";
import {
  markAsRead,
  markAllAsRead,
  deleteNotification,
  subscribeToUserNotifications,
} from "../services/notificationService";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getNotificationPermissionStatus,
  isNotificationSupported,
  requestNotificationPermission,
} from "../services/pushNotificationService";

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enablingBrowserNotifications, setEnablingBrowserNotifications] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(
    getNotificationPermissionStatus
  );
  const dropdownRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const prevUnreadCountRef = useRef(0);
  const hasLoadedNotificationsRef = useRef(false);

  // Setup real-time listener for notifications
  useEffect(() => {
    const userId = user?.id || user?.uid;
    if (userId) {
      setLoading(true);

      // Subscribe to real-time notification updates
      unsubscribeRef.current = subscribeToUserNotifications(
        userId,
        (notifs, unread) => {
          setNotifications(notifs);
          setUnreadCount(unread);
          setLoading(false);

          if (!hasLoadedNotificationsRef.current) {
            prevUnreadCountRef.current = unread;
            hasLoadedNotificationsRef.current = true;
            return;
          }

          // Show toast notification when new unread notifications arrive
          if (unread > prevUnreadCountRef.current) {
            const newNotifications = notifs.filter((n) => !n.isRead);
            if (newNotifications.length > 0) {
              const latestNotif = newNotifications[0];
              toast.info(
                `📢 ${latestNotif.actorName} (${latestNotif.actorRole}): ${latestNotif.message}`,
                {
                  position: "top-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                }
              );

            }
          }
          prevUnreadCountRef.current = unread;
        },
        (error) => {
          console.error("Error listening to notifications:", error);
          setLoading(false);
        }
      );

      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
        }
        hasLoadedNotificationsRef.current = false;
        prevUnreadCountRef.current = 0;
      };
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId);
    // Real-time listener will automatically update
  };

  const handleMarkAllAsRead = async () => {
    const userId = user?.id || user?.uid;
    await markAllAsRead(userId);
    // Real-time listener will automatically update
  };

  const handleDelete = async (notificationId) => {
    await deleteNotification(notificationId);
    // Real-time listener will automatically update
  };

  const getSafeRouteForCurrentUser = (route) => {
    const normalizedRoute = route || "/dashboard";
    const role = user?.role;

    if (role === "admin") {
      return normalizedRoute;
    }

    if (role !== "tutor") {
      return "/dashboard";
    }

    const tutorRoutes = new Set([
      "/dashboard",
      "/tasks",
      "/projects",
      "/skills",
      "/courses",
      "/lessons",
      "/classes",
      "/materials",
      "/students",
      "/batches",
      "/attendance",
      "/leaves",
      "/colleges",
      "/profile",
      "/holidays",
    ]);

    return tutorRoutes.has(normalizedRoute) ? normalizedRoute : "/dashboard";
  };

  const getNotificationRoute = (notification) => {
    if (notification.metadata?.route) {
      return getSafeRouteForCurrentUser(notification.metadata.route);
    }

    const type = notification.type || "";
    const actionType = notification.actionType || "";
    const action = notification.metadata?.action || "";

    if (type === "task" || actionType === "task_assigned" || action.includes("task")) {
      return getSafeRouteForCurrentUser("/tasks");
    }

    if (type === "leave" || action.includes("leave")) {
      return getSafeRouteForCurrentUser("/leaves");
    }

    if (type === "check_in" || type === "check_out" || type === "attendance") {
      return getSafeRouteForCurrentUser("/attendance");
    }

    if (type === "skill") {
      return getSafeRouteForCurrentUser(
        notification.actorRole === "tutor" ? "/tutors" : "/students"
      );
    }

    if (type === "fee" || action.includes("fee") || action.includes("payment")) {
      return getSafeRouteForCurrentUser("/fees");
    }

    if (type === "salary_payment_recorded" || type === "salary") {
      return getSafeRouteForCurrentUser("/salary");
    }

    return "/dashboard";
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    setIsOpen(false);
    if (user?.role !== "admin" && user?.role !== "tutor") {
      return;
    }

    navigate(getNotificationRoute(notification), {
      state: {
        notificationId: notification.id,
        notificationMetadata: notification.metadata || {},
      },
    });
  };

  const handleEnableBrowserNotifications = async () => {
    const userId = user?.id || user?.uid;
    if (!userId) return;

    setEnablingBrowserNotifications(true);
    try {
      await requestNotificationPermission(userId);
      setPermissionStatus(getNotificationPermissionStatus());
      toast.success("Browser notifications enabled.");
    } catch (error) {
      setPermissionStatus(getNotificationPermissionStatus());
      toast.error(error.message || "Unable to enable browser notifications.");
    } finally {
      setEnablingBrowserNotifications(false);
    }
  };

  const getNotificationIcon = (type) => {
    const iconClass = "w-4 h-4";
    switch (type) {
      case "check_in":
        return <span className={`${iconClass} text-green-500`}>🟢</span>;
      case "check_out":
        return <span className={`${iconClass} text-red-500`}>🔴</span>;
      case "task_completed":
        return <span className={`${iconClass} text-blue-500`}>✅</span>;
      case "task_assigned":
        return <span className={`${iconClass} text-purple-500`}>📋</span>;
      case "login":
        return <span className={`${iconClass} text-indigo-500`}>🔐</span>;
      default:
        return <span className={`${iconClass} text-gray-500`}>📢</span>;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "student":
        return "primary";
      case "tutor":
        return "warning";
      case "employee":
        return "info";
      default:
        return "secondary";
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 shadow-2xl rounded-lg z-50 max-h-[32rem] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-1"
                  title="Mark all as read"
                >
                  <CheckCheck size={14} />
                  <span>Mark all read</span>
                </button>
              )}
            </div>
          </div>

          {isNotificationSupported() && permissionStatus !== "granted" && (
            <div className="p-3 border-b border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40">
              <p className="mb-2 text-xs text-amber-900 dark:text-amber-200">
                {permissionStatus === "denied"
                  ? "Browser notifications are blocked. Allow them in this site's browser settings, then try again."
                  : "Enable browser notifications to receive alerts when this tab is in the background."}
              </p>
              <button
                type="button"
                onClick={handleEnableBrowserNotifications}
                disabled={enablingBrowserNotifications}
                className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {enablingBrowserNotifications ? "Enabling..." : "Enable browser notifications"}
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Bell size={48} className="mx-auto mb-2 opacity-30" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y dark:divide-gray-700">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(notification)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleNotificationClick(notification);
                      }
                    }}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      !notification.isRead ? "bg-indigo-50 dark:bg-indigo-900/20" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-800 dark:text-white text-sm">
                                {notification.actorName}
                              </span>
                              <Badge bg={getRoleBadgeColor(notification.actorRole)} className="text-xs">
                                {notification.actorRole}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {notification.createdAt
                                ? formatDistanceToNow(notification.createdAt, {
                                    addSuffix: true,
                                  })
                                : "Just now"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            {!notification.isRead && (
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleMarkAsRead(notification.id);
                                }}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                title="Mark as read"
                              >
                                <Check size={14} className="text-green-600 dark:text-green-400" />
                              </button>
                            )}
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(notification.id);
                              }}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                              title="Delete"
                            >
                              <X size={14} className="text-red-600 dark:text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing last {notifications.length} notifications
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
